import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
  TablePagination,
} from '@mui/material';
import EmptyState from './EmptyState';
import DetailSection from './DetailSection';

export interface Column<T> {
  id: keyof T | string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any, row: T) => React.ReactNode;
  hideOnMobile?: boolean;
  priority?: number; // Lower number = higher priority (shown first on mobile)
  /** On mobile, push this field into a collapsible "details" accordion inside the card. */
  detail?: boolean;
}

interface ResponsiveTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  loadingRows?: number;
  emptyMessage?: string;
  keyField: keyof T;
  onRowClick?: (row: T) => void;
  // Pagination
  pagination?: boolean;
  page?: number;
  rowsPerPage?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
  // Mobile specific
  mobileCardRender?: (row: T, columns: Column<T>[]) => React.ReactNode;
  stickyHeader?: boolean;
  /** Per-row actions — rendered as a trailing cell (desktop) and a card footer (mobile). */
  renderActions?: (row: T) => React.ReactNode;
  /** Header label for the actions column. Default 'عملیات'. */
  actionsLabel?: string;
  /** Label for the mobile details accordion. Default 'جزئیات بیشتر'. */
  detailsLabel?: string;
}

function ResponsiveTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  loadingRows = 5,
  emptyMessage = 'داده‌ای یافت نشد',
  keyField,
  onRowClick,
  pagination = false,
  page = 0,
  rowsPerPage = 10,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50],
  mobileCardRender,
  stickyHeader = false,
  renderActions,
  actionsLabel = 'عملیات',
  detailsLabel = 'جزئیات بیشتر',
}: ResponsiveTableProps<T>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const cardBorder = `1px solid ${theme.palette.divider}`;

  // Filter columns based on visibility settings
  const visibleColumns = columns.filter((col) => {
    if (isMobile && col.hideOnMobile) return false;
    return true;
  });

  // Sort columns by priority for mobile view
  const sortedColumns = [...visibleColumns].sort((a, b) => {
    const priorityA = a.priority ?? 999;
    const priorityB = b.priority ?? 999;
    return priorityA - priorityB;
  });
  const mainColumns = sortedColumns.filter((c) => !c.detail);
  const detailColumns = sortedColumns.filter((c) => c.detail);

  const getValue = (row: T, columnId: string): any => {
    const keys = columnId.split('.');
    let value: any = row;
    for (const key of keys) {
      value = value?.[key];
    }
    return value;
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    onPageChange?.(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange?.(parseInt(event.target.value, 10));
    onPageChange?.(0);
  };

  // Loading skeleton
  if (loading) {
    if (isMobile) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Array.from({ length: loadingRows }).map((_, index) => (
            <Paper key={index} sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton variant="text" width="80%" height={20} />
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Skeleton variant="rounded" width={60} height={28} />
                  <Skeleton variant="rounded" width={60} height={28} />
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell key={String(column.id)} align={column.align || 'right'}>
                  <Skeleton variant="text" width={80} />
                </TableCell>
              ))}
              {renderActions && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: loadingRows }).map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                {visibleColumns.map((column) => (
                  <TableCell key={String(column.id)}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
                {renderActions && (
                  <TableCell>
                    <Skeleton variant="rounded" width={80} height={28} />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <Paper sx={{ borderRadius: 2 }}>
        <EmptyState variant="no-results" title={emptyMessage} dense />
      </Paper>
    );
  }

  // Mobile Card View
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data.map((row) => {
          if (mobileCardRender) {
            return (
              <Box
                key={String(row[keyField])}
                onClick={() => onRowClick?.(row)}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {mobileCardRender(row, columns)}
              </Box>
            );
          }

          return (
            <Paper
              key={String(row[keyField])}
              sx={{
                p: 2,
                borderRadius: 2,
                transition: 'all 0.2s ease',
              }}
            >
              <Box
                onClick={() => onRowClick?.(row)}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {mainColumns.map((column, index) => {
                  const value = getValue(row, String(column.id));
                  const formattedValue = column.format ? column.format(value, row) : value;

                  return (
                    <Box
                      key={String(column.id)}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1,
                        borderBottom: index < mainColumns.length - 1 ? cardBorder : 'none',
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500, flexShrink: 0 }}
                      >
                        {column.label}
                      </Typography>
                      <Box sx={{ textAlign: 'end', minWidth: 0 }}>{formattedValue ?? '-'}</Box>
                    </Box>
                  );
                })}
              </Box>

              {detailColumns.length > 0 && (
                <Box sx={{ mt: 1.25 }}>
                  <DetailSection
                    title={detailsLabel}
                    rows={detailColumns.map((column) => {
                      const value = getValue(row, String(column.id));
                      return {
                        label: column.label,
                        value: column.format ? column.format(value, row) : value,
                      };
                    })}
                  />
                </Box>
              )}

              {renderActions && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: cardBorder,
                  }}
                >
                  {renderActions(row)}
                </Box>
              )}
            </Paper>
          );
        })}

        {pagination && totalCount !== undefined && (
          <Box sx={{ mt: 1 }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={rowsPerPageOptions}
              labelRowsPerPage="تعداد در صفحه:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} از ${count !== -1 ? count : `بیش از ${to}`}`
              }
              sx={{
                '.MuiTablePagination-toolbar': {
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 1,
                },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  margin: 0,
                },
              }}
            />
          </Box>
        )}
      </Box>
    );
  }

  // Desktop/Tablet Table View
  return (
    <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: stickyHeader ? 600 : undefined }}>
        <Table stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={String(column.id)}
                  align={column.align || 'right'}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
              {renderActions && (
                <TableCell align="center" style={{ width: 1, whiteSpace: 'nowrap' }}>
                  {actionsLabel}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow
                key={String(row[keyField])}
                hover
                onClick={() => onRowClick?.(row)}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
              >
                {visibleColumns.map((column) => {
                  const value = getValue(row, String(column.id));
                  const formattedValue = column.format ? column.format(value, row) : value;

                  return (
                    <TableCell key={String(column.id)} align={column.align || 'right'}>
                      {formattedValue ?? '-'}
                    </TableCell>
                  );
                })}
                {renderActions && (
                  <TableCell
                    align="center"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                      {renderActions(row)}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && totalCount !== undefined && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
          labelRowsPerPage="تعداد در صفحه:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} از ${count !== -1 ? count : `بیش از ${to}`}`
          }
        />
      )}
    </Paper>
  );
}

export default ResponsiveTable;
